import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import {
  ActivityItem, Branding, ChannelContextModel, ChannelYouTubeInfo, ConfigurationState, DiningLocation, MailFeedModel, MealItem,
  NewsItem, PriorityNewsItem, ScheduledActivity, ServiceTypeItem, SignageChangeModel, SignageDataSource, WeatherResponse
} from '@cubigo/digital-signage';
import { LocalDate } from '@js-joda/core';

import PubNub from 'pubnub';
import { Observable, Subscription } from 'rxjs';

import { SIGNAGE_API } from 'app/shared/interceptors/signage-api-context';
import { environment } from 'environments/environment';

import { ConfigDataService } from './config-data.service';
import { SignageChangeParserService } from './signage-change-parser.service';
import { SIGNAGE_PUSH_CLIENT } from './signage-push';

@Injectable()
export class HttpSignageDataSourceService extends SignageDataSource {
  private readonly changeParser = inject(SignageChangeParserService);
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigDataService);
  private readonly createPushClient = inject(SIGNAGE_PUSH_CLIENT, { optional: true })
    ?? ((uuid: string): PubNub => new PubNub({
      publishKey: environment.pubnub.pubKey, subscribeKey: environment.pubnub.subKey, uuid
    }));

  public override async loadConfiguration(signal: AbortSignal): Promise<ConfigurationState> {
    const configuration = await this.get<ConfigurationState>('Services/SyndicatorConfiguration', signal);

    if (signal.aborted) {
      throw this.abortError();
    }

    if (!configuration?.channel?.providerUuid?.trim() || !Number.isInteger(configuration.channel.id)
      || configuration.channel.id <= 0 || !configuration.owner?.uuid?.trim()) {
      throw new Error('invalid-config');
    }

    this.config.configuration.set(configuration);

    return configuration;
  }

  public override loadBranding(signal: AbortSignal): Promise<Branding> {
    return this.get('Branding/community', signal);
  }

  public override loadServiceNames(signal: AbortSignal): Promise<ServiceTypeItem[]> {
    return this.get('Syndication/Service/ServiceNames', signal);
  }

  public override loadNews(context: ChannelContextModel, signal: AbortSignal): Promise<NewsItem[]> {
    return this.get(this.channelPath('News', context, 'NewsItems'), signal);
  }

  public override loadBirthdays(context: ChannelContextModel, signal: AbortSignal): Promise<NewsItem[]> {
    // -1 is the API's dedicated birthday feed, not a display channel.
    return this.get(this.channelPath('News', { ...context, id: -1 }, 'NewsItems'), signal);
  }

  public override loadPriorityNews(context: ChannelContextModel, signal: AbortSignal): Promise<PriorityNewsItem[]> {
    return this.get(this.channelPath('News', context, 'CurrentPriorityNews'), signal);
  }

  public override loadActivities(context: ChannelContextModel, on: LocalDate, signal: AbortSignal): Promise<ActivityItem[]> {
    return this.get(this.channelPath('Activities', context, `Activities/On/${on}`), signal);
  }

  public override loadScheduledVideos(context: ChannelContextModel, signal: AbortSignal): Promise<ScheduledActivity[]> {
    return this.get(this.channelPath('YouTubeActivities', context, 'YouTubeActivities'), signal);
  }

  public override loadDiningLocations(context: ChannelContextModel, signal: AbortSignal): Promise<DiningLocation[]> {
    return this.get(`Syndication/Dining/Provider/${encodeURIComponent(context.providerUuid)}/locations`, signal);
  }

  public override loadMeals(context: ChannelContextModel, on: LocalDate, signal: AbortSignal): Promise<MealItem[]> {
    return this.get(this.channelPath('Dining', context, `Meals/On/${on}`), signal);
  }

  public override loadWeather(signal: AbortSignal): Promise<WeatherResponse> {
    return this.get('OpenWeatherMap/GetCommunityWeather', signal);
  }

  public override loadMail(signal: AbortSignal): Promise<MailFeedModel> {
    return this.get('syndication/mail', signal);
  }

  public override loadLiveVideo(context: ChannelContextModel, signal: AbortSignal): Promise<ChannelYouTubeInfo> {
    return this.get(`Services/ProviderChannel/${context.id}/YouTubeInfo`, signal);
  }

  public override changes(context: ChannelContextModel): Observable<SignageChangeModel> {
    return new Observable(subscriber => {
      const ownerUuid = this.config.configuration()?.owner?.uuid;

      if (!ownerUuid) {
        subscriber.error(new Error('Push requires a loaded display configuration'));

        return undefined;
      }

      const client = this.createPushClient(ownerUuid);
      const provider = context.providerUuid.replaceAll('-', '');
      const priorityChannel = `${provider}.${context.id}.ItemPublished`;
      const liveChannel = `${provider}.ProviderChannel.${context.id}`;
      const listener: PubNub.ListenerParameters = {
        message: event => {
          if (event.channel !== priorityChannel && event.channel !== liveChannel) {
            return;
          }

          const change = this.changeParser.parse(event.message, context, event.channel === priorityChannel);

          if (change) {
            subscriber.next(change);
          }
        }
      };
      const dispose = (): void => {
        client.removeListener(listener);
        client.unsubscribeAll();
        client.stop();
      };
      subscriber.add(dispose);
      client.addListener(listener);
      client.subscribe({ channels: [priorityChannel, liveChannel] });

      return undefined;
    });
  }

  private channelPath(feed: string, context: ChannelContextModel, resource: string): string {
    return `Syndication/${feed}/Provider/${encodeURIComponent(context.providerUuid)}/${resource}?channelId=${context.id}`;
  }

  private get<T>(path: string, signal: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (signal.aborted) {
        reject(this.abortError());

        return;
      }

      const subscription = new Subscription();
      const abort = (): void => {
        subscription.unsubscribe();
        reject(this.abortError());
      };
      signal.addEventListener('abort', abort, { once: true });
      subscription.add(() => signal.removeEventListener('abort', abort));
      subscription.add(this.http.get<T>(`${environment.azureApiManagementUrl}${path}`, {
        context: new HttpContext().set(SIGNAGE_API, true)
      }).subscribe({
        next: value => { resolve(value); subscription.unsubscribe(); },
        error: error => { reject(error); subscription.unsubscribe(); },
        complete: () => { subscription.unsubscribe(); }
      }));
    });
  }

  private abortError(): Error {
    const error = new Error('Signage request aborted');
    error.name = 'AbortError';

    return error;
  }
}
